import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useStaticQuery, graphql } from "gatsby";
import "./userSurvey.scss";

const SURVEY_COOKIE_NAME = "idmc_survey_completed";
const SURVEY_DISMISSED_COOKIE_NAME = "idmc_survey_dismissed";
const SURVEY_FORM_DATA_KEY = "idmc_survey_form_data";
const PAGE_COUNT_KEY = "idmc_page_count";

// Helper functions for cookie/localStorage management
const setCookieWithExpiry = (name, value, days) => {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + days);
  localStorage.setItem(name, JSON.stringify({
    value,
    expiry: expiryDate.toISOString()
  }));
};

const getCookieWithExpiry = (name) => {
  const item = localStorage.getItem(name);
  if (!item) return null;
  
  try {
    const data = JSON.parse(item);
    const expiry = new Date(data.expiry);
    if (new Date() > expiry) {
      localStorage.removeItem(name);
      return null;
    }
    return data.value;
  } catch (e) {
    return null;
  }
};

const UserSurvey = () => {
  const [config, setConfig] = useState(null);
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({
    q1: "",
    q2: "",
    q3: "",
  });
  // Q3 is now always shown, no need for conditional state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const timeTriggerRef = useRef(null);
  const pageCountRef = useRef(0);
  const scrollTriggeredRef = useRef(false);

  // Load saved form data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(SURVEY_FORM_DATA_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed);
      } catch (e) {
        console.error("Error loading saved form data:", e);
      }
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (formData.q1 || formData.q2 || formData.q3) {
      localStorage.setItem(SURVEY_FORM_DATA_KEY, JSON.stringify(formData));
    }
  }, [formData]);

  // Fetch survey configuration from GraphQL (build-time data)
  const surveyData = useStaticQuery(graphql`
    query SurveyConfigQuery {
      surveyConfig {
        enabled
        heading
        intro_text
        success_title
        success_message
        questions {
          id
          label
          type
          options
          required
          conditional
          show_when {
            question_id
            answer_value
          }
        }
        triggers {
          time_seconds
          page_navigation
          scroll_threshold
        }
      }
    }
  `);

  // Set config from GraphQL data
  useEffect(() => {
    if (surveyData?.surveyConfig && surveyData.surveyConfig.enabled) {
      setConfig(surveyData.surveyConfig);
    }
  }, [surveyData]);

  // Check if survey should be shown
  useEffect(() => {
    if (!config) return;

    // Don't show if already completed (30 days)
    const completed = getCookieWithExpiry(SURVEY_COOKIE_NAME);
    if (completed) {
      return;
    }

    // Don't show if dismissed within last 24 hours
    const dismissed = getCookieWithExpiry(SURVEY_DISMISSED_COOKIE_NAME);
    if (dismissed) {
      return;
    }

    // Get page count from sessionStorage
    const pageCount = parseInt(sessionStorage.getItem(PAGE_COUNT_KEY) || "0", 10);
    pageCountRef.current = pageCount + 1;
    sessionStorage.setItem(PAGE_COUNT_KEY, pageCountRef.current.toString());

    // Time-based trigger
    if (config.triggers.time_seconds > 0) {
      timeTriggerRef.current = setTimeout(() => {
        checkAndShowSurvey();
      }, config.triggers.time_seconds * 1000);
    }

    // Page navigation trigger (2nd page + scroll)
    if (config.triggers.page_navigation && pageCountRef.current >= 2) {
      const handleScroll = () => {
        if (scrollTriggeredRef.current) return;

        const scrollPercentage =
          (window.scrollY + window.innerHeight) /
          document.documentElement.scrollHeight;

        if (scrollPercentage >= config.triggers.scroll_threshold / 100) {
          scrollTriggeredRef.current = true;
          window.removeEventListener("scroll", handleScroll);
          checkAndShowSurvey();
        }
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      
      // Check initial scroll position
      handleScroll();

      return () => {
        window.removeEventListener("scroll", handleScroll);
        if (timeTriggerRef.current) {
          clearTimeout(timeTriggerRef.current);
        }
      };
    }

    return () => {
      if (timeTriggerRef.current) {
        clearTimeout(timeTriggerRef.current);
      }
    };
  }, [config]);

  const checkAndShowSurvey = () => {
    // Clear time trigger if it exists
    if (timeTriggerRef.current) {
      clearTimeout(timeTriggerRef.current);
      timeTriggerRef.current = null;
    }

    // Don't show if already completed (30 days)
    const completed = getCookieWithExpiry(SURVEY_COOKIE_NAME);
    if (completed) {
      return;
    }

    // Don't show if dismissed within last 24 hours
    const dismissed = getCookieWithExpiry(SURVEY_DISMISSED_COOKIE_NAME);
    if (dismissed) {
      return;
    }

    setShow(true);
  };

  // Handle form changes
  const handleChange = (questionId, value) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  // Track event to GA4/GTM
  const trackSurveyEvent = (eventName, eventData) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventData,
      });
    }

    // Also try gtag if available
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, eventData);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Track survey submission to GTM
    trackSurveyEvent("survey_submitted", {
      survey_type: "user_feedback",
      user_type: formData.q1,
      found_information: formData.q2,
      what_looking_for: formData.q3 || "N/A",
      page_url: window.location.pathname,
    });

    // Submit to Drupal API
    try {
      const drupalBaseUrl = (process.env.GATSBY_DRUPAL_URL || '').replace(/\/+$/, ''); // Remove trailing slashes
      const apiUrl = `${drupalBaseUrl}/api/survey-submit`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_type: formData.q1,
          found_information: formData.q2,
          what_looking_for: formData.q3 || '',
          page_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to save survey submission');
      }

      // Log success (optional, for debugging)
      console.log('Survey submission saved to Drupal:', result.id);
    } catch (error) {
      // Log error but don't block user experience
      // Survey is still tracked in GTM, so we continue with success flow
      console.error('Error saving survey to Drupal:', error);
    }

    // Mark as completed (don't show again for 30 days)
    setCookieWithExpiry(SURVEY_COOKIE_NAME, "true", 30);
    
    // Clear saved form data after successful submission
    localStorage.removeItem(SURVEY_FORM_DATA_KEY);

    // Show success message
    setIsSubmitting(false);
    setIsSubmitted(true);

    // Close modal after 5 seconds (increased from 2 seconds for better UX)
    setTimeout(() => {
      setShow(false);
      setIsSubmitted(false);
      // Reset form after closing
      setFormData({ q1: "", q2: "", q3: "" });
    }, 5000);
  };

  // Handle close/dismiss
  const handleClose = () => {
    // Track dismissal
    trackSurveyEvent("survey_dismissed", {
      survey_type: "user_feedback",
      page_url: window.location.pathname,
    });

    // Mark as dismissed (don't show again for 24 hours)
    setCookieWithExpiry(SURVEY_DISMISSED_COOKIE_NAME, "true", 1);

    setShow(false);
  };

  if (!config || !show) {
    return null;
  }

  const q1Question = config.questions.find((q) => q.id === "q1");
  const q2Question = config.questions.find((q) => q.id === "q2");
  const q3Question = config.questions.find((q) => q.id === "q3");

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
      className="user-survey-modal"
      size="md"
    >
      <Modal.Header closeButton className="user-survey-header">
        <div className="user-survey-header-content">
          <img
            src="/icons/idmc-regalBlue-logo.svg"
            alt="IDMC Logo"
            className="user-survey-logo"
          />
          <Modal.Title>{config.heading}</Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body>
        {isSubmitted ? (
          <div className="user-survey-success">
            <div className="user-survey-success-icon">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" fill="#28a745" opacity="0.1" />
                <path
                  d="M9 12l2 2 4-4"
                  stroke="#28a745"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="12" r="10" stroke="#28a745" strokeWidth="2" />
              </svg>
            </div>
            <h3 className="user-survey-success-title">{config.success_title || 'Thank you!'}</h3>
            <p className="user-survey-success-message">
              {config.success_message || 'Your feedback helps us improve our website and make our data and analysis easier to find.'}
            </p>
          </div>
        ) : (
          <>
            <p className="user-survey-intro">{config.intro_text}</p>
            <Form onSubmit={handleSubmit}>
              {/* Question 1 - Select/Dropdown */}
              {q1Question && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{q1Question.label}</Form.Label>
                  <Form.Select
                    value={formData.q1}
                    onChange={(e) => handleChange("q1", e.target.value)}
                    required={q1Question.required}
                    aria-label={q1Question.label}
                  >
                    <option value="">-- Select an option --</option>
                    {q1Question.options.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {/* Question 2 */}
              {q2Question && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{q2Question.label}</Form.Label>
                  {q2Question.options.map((option, index) => (
                    <Form.Check
                      key={index}
                      type="radio"
                      id={`q2-${index}`}
                      name="q2"
                      label={option}
                      value={option}
                      checked={formData.q2 === option}
                      onChange={(e) => handleChange("q2", e.target.value)}
                      required={q2Question.required}
                    />
                  ))}
                </Form.Group>
              )}

              {/* Question 3 - Always shown, optional */}
              {q3Question && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{q3Question.label}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.q3}
                    onChange={(e) => handleChange("q3", e.target.value)}
                    placeholder="Please tell us what you were looking for..."
                    required={q3Question.required || false}
                  />
                </Form.Group>
              )}

              <div className="d-flex justify-content-center mt-4">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting || !formData.q1 || !formData.q2}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default UserSurvey;

